import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft, faPhone, faGlobe, faClock, faMapMarkerAlt, faLocationArrow, faMapMarkedAlt, faThumbsUp, faDollarSign, faWrench,
  faExclamationTriangle, faImages,
} from '@fortawesome/free-solid-svg-icons'
import Carousel, { Modal, ModalGateway } from 'react-images'

import Header from '../../components/header/index'
import Footer from '../../components/footer/index'
import { getSafeSiteUrl } from '../../../utils/safeUrl'
import services from '../../../services/api'
import { softCase } from './List'
import StarRating, { formatRating } from './StarRating'
import { formatPhone, formatPercent, formatHour, surveyMetrics } from './companyFormat'
import './details.css'

// Distribuicao da nota: chave da contagem e da porcentagem que a pesquisa devolve.
const DISTRIBUTION = [
  { stars: 5, count: 'five', progress: 'progressFive' },
  { stars: 4, count: 'four', progress: 'progressFour' },
  { stars: 3, count: 'three', progress: 'progressThree' },
  { stars: 2, count: 'two', progress: 'progressTwo' },
  { stars: 1, count: 'one', progress: 'progressOne' },
]

const GRID_PHOTOS = 3

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

// Links que abrem o app de navegacao no celular (ou o site no computador).
// Coordenadas quando existem (mais precisas); senao, o endereco.
function navigationLinks(company) {
  const lat = Number(company.latitude)
  const lng = Number(company.longitude)
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
  const address = encodeURIComponent(
    [company.street, company.streetNumber, company.neighborhood, company.city, company.state].filter(Boolean).join(', ')
  )
  return {
    waze: hasCoords
      ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`
      : `https://waze.com/ul?q=${address}&navigate=yes`,
    googleMaps: hasCoords
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : `https://www.google.com/maps/dir/?api=1&destination=${address}`,
  }
}

// "00:00 às 00:00" e horario nao cadastrado, nao loja aberta 24h.
const hasHours = (company) => [company.openAt, company.closesAt].some(h => h && formatHour(h) !== '00:00')

function useCompanyDetails(sourceId, id) {
  const [state, setState] = useState({ loading: true, error: false, company: null, survey: null, surveyFailed: false })

  useEffect(() => {
    let active = true
    setState({ loading: true, error: false, company: null, survey: null, surveyFailed: false })

    const load = async () => {
      let company
      try {
        const { data } = await services.api.get(services.config.endpoint + `companies/get-data-provider/${sourceId}/${id}`)
        company = data
      } catch (err) {
        if (active) setState({ loading: false, error: true, company: null, survey: null, surveyFailed: false })
        return
      }

      // Avaliacoes sao opcionais: se falharem, a pagina mostra a empresa e avisa so nessa secao.
      let survey = null
      let surveyFailed = false
      try {
        const res = await services.api.get(
          services.config.endpoint + `satisfaction-survey/by-company-source/${company.cpfCnpj}/${sourceId}`
        )
        survey = res.data
      } catch (err) {
        surveyFailed = true
      }
      if (active) setState({ loading: false, error: false, company, survey, surveyFailed })
    }

    load()
    return () => { active = false }
  }, [sourceId, id])

  return state
}

function Reviews({ company, survey, surveyFailed, total }) {
  const [starFilter, setStarFilter] = useState(null)
  const evaluations = useMemo(() => (survey && Array.isArray(survey.avaliacoes) ? survey.avaliacoes : []), [survey])
  const metrics = evaluations.length ? surveyMetrics(evaluations) : null
  const shown = starFilter ? evaluations.filter(e => Math.round(Number(e.Average_Ratings)) === starFilter) : evaluations

  return (
    <section id='avaliacoes' className='dc-section' aria-labelledby='dc-reviews-title'>
      <h2 id='dc-reviews-title'>Avaliações</h2>

      {total === 0 && <p className='dc-muted'>Esta empresa ainda não recebeu avaliações.</p>}

      {total > 0 && surveyFailed &&
        <p className='dc-notice'>
          <FontAwesomeIcon icon={faExclamationTriangle} aria-hidden='true' />
          Não foi possível carregar as avaliações agora. Tente novamente em instantes.
        </p>
      }

      {total > 0 && !surveyFailed &&
        <>
          <div className='dc-review-summary'>
            <div className='dc-review-score'>
              <strong>{formatRating(company.rating) || '—'}</strong>
              <StarRating value={company.rating} size='md' />
              <span>{plural(total, 'avaliação', 'avaliações')}</span>
            </div>

            <ul className='dc-distribution' aria-label='Filtrar avaliações por nota'>
              {DISTRIBUTION.map(row => {
                const count = Number(survey[row.count]) || 0
                const active = starFilter === row.stars
                return (
                  <li key={row.stars}>
                    <button
                      type='button'
                      aria-pressed={active}
                      aria-label={`${row.stars} estrelas: ${plural(count, 'avaliação', 'avaliações')}`}
                      disabled={count === 0}
                      onClick={() => setStarFilter(active ? null : row.stars)}
                    >
                      <span className='dc-distribution-label'>{row.stars}</span>
                      <span className='dc-distribution-bar'><span style={{ width: `${Number(survey[row.progress]) || 0}%` }} /></span>
                      <span className='dc-distribution-count'>{count}</span>
                    </button>
                  </li>
                )
              })}
            </ul>

            {metrics &&
              <dl className='dc-metrics'>
                <div>
                  <dt><FontAwesomeIcon icon={faThumbsUp} aria-hidden='true' />Indicaria</dt>
                  <dd>{formatPercent(metrics.ratingRecommendation)}</dd>
                </div>
                <div>
                  <dt><FontAwesomeIcon icon={faDollarSign} aria-hidden='true' />Preço</dt>
                  <dd><StarRating value={metrics.ratingPrice} /></dd>
                </div>
                <div>
                  <dt><FontAwesomeIcon icon={faWrench} aria-hidden='true' />Serviço</dt>
                  <dd><StarRating value={metrics.ratingProductService} /></dd>
                </div>
              </dl>
            }
          </div>

          {starFilter &&
            <p className='dc-filter-note'>
              Mostrando avaliações de {plural(starFilter, 'estrela', 'estrelas')}.{' '}
              <button type='button' onClick={() => setStarFilter(null)}>Ver todas</button>
            </p>
          }

          <ul className='dc-review-list'>
            {shown.map((review, index) => (
              <li key={review.id || `${review.Date_Evaluation}-${index}`} className='dc-review'>
                <header>
                  <strong>{softCase(review.Name) || 'Cliente'}</strong>
                  <StarRating value={review.Average_Ratings} />
                </header>
                {review.Comment && <p className='dc-review-comment'>{review.Comment}</p>}
                <p className='dc-review-meta'>
                  <span className={review.Would_Indicate ? 'dc-positive' : 'dc-negative'}>
                    {review.Would_Indicate ? 'Indicaria' : 'Não indicaria'}
                  </span>
                  {review.Date_Evaluation && <span>Avaliado em {review.Date_Evaluation}</span>}
                </p>
              </li>
            ))}
            {!shown.length && <li className='dc-muted'>Nenhuma avaliação para mostrar.</li>}
          </ul>
        </>
      }
    </section>
  )
}

export default function DetailsCompany() {
  const { sourceId, id } = useParams()
  const selectedCity = useSelector(state => state.provider.selectedCity)
  const { loading, error, company, survey, surveyFailed } = useCompanyDetails(sourceId, id)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const hasSearchCity = !!(selectedCity && selectedCity.id)
  // Sem busca anterior, a busca e a da cidade da propria oficina.
  const companyCity = company && company.city && company.state
    ? { id: `empresa-${company.state}-${company.city}`.toLowerCase(), Name: softCase(company.city), States: { Initials: company.state } }
    : null
  const backTo = hasSearchCity || companyCity ? '/companies' : '/'

  // 1. Veio da lista: volta pelo historico (mesma cidade, filtros e rolagem).
  // 2. Ja havia uma cidade buscada: vai para a busca dela.
  // 3. Aberto direto, sem busca: vai para a busca da cidade da oficina.
  const goBack = (event) => {
    if (location.state && location.state.fromSearch) {
      event.preventDefault()
      history.goBack()
      return
    }
    if (!hasSearchCity && companyCity) {
      dispatch({ type: 'HANDLE_CHANGE_CITY', payload: companyCity })
    }
  }

  if (loading || error || !company) {
    return (
      <div className='dc-page'>
        <Header contained />
        <main className='dc-main dc-state' aria-busy={loading}>
          {loading
            ? <p>Carregando dados da empresa…</p>
            : <>
              <FontAwesomeIcon icon={faExclamationTriangle} aria-hidden='true' />
              <p>Não foi possível carregar os dados da empresa. Tente novamente em instantes.</p>
              <Link to={backTo} className='dc-back'>Voltar para a busca</Link>
            </>
          }
        </main>
        <Footer contained />
      </div>
    )
  }

  const name = softCase(company.tradingName || company.name)
  const reviews = Number((survey && survey.amountOfSatisfactionSurvey) ?? company.amountOfSatisfactionSurvey) || 0
  const photos = (company.Files || []).filter(f => f && f.file).map(f => ({ src: f.file }))
  const phone = formatPhone(company.Phones)
  const site = getSafeSiteUrl(company.site)
  const street = [softCase(company.street), company.streetNumber].filter(Boolean).join(', ')
  const cityLine = [softCase(company.neighborhood), [softCase(company.city), company.state].filter(Boolean).join(' - ')].filter(Boolean).join(' · ')
  const fullAddress = [street, cityLine].filter(Boolean).join(' · ')
  const navigation = navigationLinks(company)
  const isPartsStore = company.Branches && company.Branches.description === 'Autopeças'
  const services_ = isPartsStore ? [{ id: 'pecas', description: 'Venda de peças' }] : (company.Services || [])
  const segments = company.Segments || []
  const extraPhotos = photos.length - GRID_PHOTOS
  const initial = (name || '?').trim().charAt(0).toUpperCase()

  return (
    <div className='dc-page'>
      <Header contained />

      <main className='dc-main'>
        <div className='dc-container'>
          <Link to={backTo} className='dc-back' onClick={goBack}>
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden='true' />
            Voltar para a busca
          </Link>

          <header className='dc-hero'>
            {company.logo
              ? <span className='dc-logo'><img src={company.logo} alt={`Logo de ${name}`} /></span>
              : <span className='dc-logo dc-logo-initial' aria-hidden='true'>{initial}</span>
            }
            <div className='dc-title'>
              {company.Branches && company.Branches.description &&
                <span className='dc-branch'>{company.Branches.description}</span>}
              <h1>{name}</h1>
              <p className='dc-rating-line'>
                {reviews > 0
                  ? <><StarRating value={company.rating} size='md' /><a href='#avaliacoes'>{plural(reviews, 'avaliação', 'avaliações')}</a></>
                  : <span>Sem avaliações ainda</span>}
              </p>
            </div>
          </header>

          <div className='dc-layout'>
            <aside className='dc-contact' aria-label='Contato e localização'>
              <ul className='dc-facts'>
                <li>
                  <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden='true' />
                  <span>{fullAddress}</span>
                </li>
                {hasHours(company) &&
                  <li>
                    <FontAwesomeIcon icon={faClock} aria-hidden='true' />
                    <span>Funciona das {formatHour(company.openAt)} às {formatHour(company.closesAt)}</span>
                  </li>
                }
                {site &&
                  <li>
                    <FontAwesomeIcon icon={faGlobe} aria-hidden='true' />
                    <a href={site} target='_blank' rel='noopener noreferrer'>Acessar o site</a>
                  </li>
                }
              </ul>
              {phone &&
                <a className='dc-btn dc-btn-primary' href={`tel:+55${phone.replace(/\D/g, '')}`}>
                  <FontAwesomeIcon icon={faPhone} aria-hidden='true' />
                  Ligar {phone}
                </a>
              }
              <div className='dc-route'>
                <span className='dc-route-label'>Como chegar</span>
                <div className='dc-route-buttons'>
                  <a className='dc-route-btn' href={navigation.waze} target='_blank' rel='noopener noreferrer'>
                    <span className='dc-route-icon dc-route-icon-waze' aria-hidden='true'>
                      <FontAwesomeIcon icon={faLocationArrow} />
                    </span>
                    Waze
                  </a>
                  <a className='dc-route-btn' href={navigation.googleMaps} target='_blank' rel='noopener noreferrer'>
                    <span className='dc-route-icon dc-route-icon-gmaps' aria-hidden='true'>
                      <FontAwesomeIcon icon={faMapMarkedAlt} />
                    </span>
                    Google Maps
                  </a>
                </div>
              </div>
            </aside>

            <div className='dc-content'>
              {photos.length > 0 &&
                <section className='dc-gallery' aria-label='Fotos'>
                  <ul className='dc-photos'>
                    {photos.map((photo, index) => (
                      <li key={photo.src + index}>
                        <button type='button' onClick={() => setLightboxIndex(index)} aria-label={`Ampliar foto ${index + 1} de ${photos.length}`}>
                          <img src={photo.src} alt='' loading='lazy' />
                          {index === GRID_PHOTOS - 1 && extraPhotos > 0 &&
                            <span className='dc-more-photos'><FontAwesomeIcon icon={faImages} aria-hidden='true' /> +{extraPhotos}</span>
                          }
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              }

              <div className='dc-panel'>
              <section className='dc-section' aria-labelledby='dc-services-title'>
                <h2 id='dc-services-title'>O que a empresa atende</h2>
                {!segments.length && !services_.length
                  ? <p className='dc-muted'>A empresa ainda não informou os serviços e os veículos que atende.</p>
                  : <>
                    {segments.length > 0 &&
                      <div className='dc-group'>
                        <h3>Veículos</h3>
                        <ul className='dc-tags'>{segments.map(s => <li key={s.id}>{s.description}</li>)}</ul>
                      </div>
                    }
                    {services_.length > 0 &&
                      <div className='dc-group'>
                        <h3>Serviços</h3>
                        <ul className='dc-tags'>{services_.map(s => <li key={s.id}>{s.description}</li>)}</ul>
                      </div>
                    }
                  </>
                }
              </section>

              <Reviews company={company} survey={survey} surveyFailed={surveyFailed} total={reviews} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <ModalGateway>
        {lightboxIndex !== null ? (
          <Modal onClose={() => setLightboxIndex(null)}>
            <Carousel views={photos} currentIndex={lightboxIndex} />
          </Modal>
        ) : null}
      </ModalGateway>

      <Footer contained />
    </div>
  )
}
